import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './TechnicalReport.css';
import CTASection from '../components/cta/CTASection';

const TechnicalReport: React.FC = () => {
  const [displayedText, setDisplayedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const fullText = 'AI-Native Clinical Trial Infrastructure for LATAM';

  useEffect(() => {
    if (displayedText.length < fullText.length) {
      const timer = setTimeout(() => {
        setDisplayedText(fullText.slice(0, displayedText.length + 1));
      }, 80);
      return () => clearTimeout(timer);
    } else {
      const cursorTimer = setTimeout(() => setShowCursor(false), 1000);
      return () => clearTimeout(cursorTimer);
    }
  }, [displayedText, fullText]);

  return (
    <div className="technical-report-page">
      {/* Hero Section */}
      <section className="report-hero">
        <div className="hero-background">
          <video autoPlay muted loop>
            <source src="/images/hero.mp4" type="video/mp4" />
          </video>
        </div>
        
        <div className="container">
          <div className="hero-content">
            <h1>
              {displayedText}
              <span className={`typewriter-cursor ${!showCursor ? 'hidden' : ''}`}>|</span>
            </h1>
            <h2>Technical Implementation and Regional Adaptation</h2>
            <p>
              A comprehensive analysis of our AI-powered platform designed specifically for Latin American clinical trials, including regulatory compliance, patient recruitment optimization, and cross-border trial management.
            </p>
            <div className="report-meta">
              <div className="meta-item">
                <span className="meta-label">Regional Focus:</span>
                <span className="meta-value">Latin America</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Countries Covered:</span>
                <span className="meta-value">12 Jurisdictions</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">AI Models Trained:</span>
                <span className="meta-value">6 AI Modules</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="report-introduction">
        <div className="container">
          <h2>Introduction</h2>
          <p>
            This technical report focuses on two main aspects: (1) our method for clustering diverse 
            clinical trial data into a unified representation that facilitates large-scale training 
            of LATAM-specific generative models, and (2) a qualitative evaluation of Lummo's 
            capabilities and limitations within the Latin American regulatory context.
          </p>
          <p>
            Previous work has studied the use of AI in clinical development using a variety of methods, 
            including recurrent neural networks, generative adversarial networks, autoregressive 
            transformers, and diffusion models. These approaches often focus on specific types of 
            clinical data or fixed study designs. In contrast, Lummo is designed as a generalist 
            model for clinical development in LATAM, capable of generating protocols spanning diverse 
            therapeutic areas, study durations, and patient populations specific to the region.
          </p>
        </div>
      </section>

      {/* Methodology Sections */}
      <section className="methodology-section">
        <div className="container">
          <h2>Transforming Study Data into Regional Clusters</h2>
          <div className="methodology-content">
            <div className="method-text">
              <p>
                Our clustering approach reflects the selective "cutting" process of RNA polymerase, 
                adapted for the regulatory specificities of Latin America. We begin by compressing 
                clinical study data into a knowledge graph that captures the core characteristics 
                of each study, including specific regulatory requirements from COFEPRIS (Mexico), 
                ANVISA (Brazil), ANMAT (Argentina), and other regional agencies.
              </p>
              <p>
                At a high level, we distill clinical study data into its fundamental components, 
                like decomposing a complex structure into individual Lego blocks. We then group 
                these elements based on their core characteristics and regional regulatory context, 
                allowing us to systematically evaluate performance and adjust protocols for optimal 
                outcomes in the Latin American market.
              </p>
            </div>
            <div className="method-visual">
              <div className="clustering-diagram">
                <div className="data-input">
                  <div className="input-node">LATAM Data</div>
                  <div className="input-node">Regulations</div>
                  <div className="input-node">Populations</div>
                </div>
                <div className="cluster-node">Cluster AI</div>
                <div className="output-clusters">
                  <div className="output-node">Protocols</div>
                  <div className="output-node">Sites</div>
                  <div className="output-node">Patients</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="methodology-section">
        <div className="container">
          <h2>Defining Patient Populations in LATAM</h2>
          <div className="methodology-content">
            <div className="method-text">
              <p>
                Balancing the statistical significance of clinical trial results with patient 
                convenience is a critical aspect of trial design, especially in Latin America 
                where geographical distances and socioeconomic diversity present unique challenges.
              </p>
              <p>
                To build a patient population in Lummo, users begin by entering their LATAM-specific 
                protocol parameters. This allows adaptation of the study population to those most 
                likely to benefit from the intervention, considering factors such as geographical 
                accessibility, ethnic diversity, and socioeconomic barriers specific to the region.
              </p>
            </div>
            <div className="method-visual">
              <div className="population-map">
                <div className="input-node">Mexico</div>
                <div className="input-node">Brazil</div>
                <div className="input-node">Argentina</div>
                <div className="input-node">Colombia</div>
                <div className="input-node">Chile</div>
                <div className="input-node">Peru</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="methodology-section">
        <div className="container">
          <h2>Regulatory Risk Mitigation Network</h2>
          <div className="methodology-content">
            <div className="method-text">
              <p>
                We train a network that reduces the risk of study failure specifically for the 
                Latin American regulatory context. This network compares clusters in failed and 
                successful studies, considering the particularities of each regional regulatory 
                agency (COFEPRIS, ANVISA, ANMAT, ISP, INVIMA, etc.).
              </p>
              <p>
                Clusters that are semantically unique in failed studies are labeled with specific 
                regulatory context. This has been particularly useful for deciphering patient 
                population parameters where automatic detection and labeling of restrictive 
                eligibility criteria acts to improve patient inclusion in the cultural and 
                socioeconomic context of LATAM.
              </p>
            </div>
            <div className="method-visual">
              <div className="risk-network">
                <div className="cluster-node">Risk AI</div>
                <div className="data-input">
                  <div className="input-node">COFEPRIS</div>
                  <div className="input-node">ANVISA</div>
                  <div className="input-node">ANMAT</div>
                  <div className="input-node">ISP</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="methodology-section">
        <div className="container">
          <h2>LATAM Study Design-Based Site Selection</h2>
          <div className="methodology-content">
            <div className="method-text">
              <p>
                Lummo systematically classifies and filters sites based on Latin America-specific 
                protocol parameters. Granular semantic mapping of criteria enables site selection 
                with a significantly higher feasibility score for a particular study in the 
                regional context.
              </p>
              <p>
                Our feasibility score is a function of: (i) similarity of past patient cohorts 
                randomized at the site, (ii) site availability, (iii) site specialty, (iv) site 
                historical performance - this is the randomization rate (RAND), and (v) local 
                regulatory navigation capacity.
              </p>
            </div>
            <div className="method-visual">
              <div className="site-selection">
                <div className="site-map">
                  <div className="site-node">Tier 1</div>
                  <div className="site-node">Tier 2</div>
                  <div className="site-node">Tier 3</div>
                </div>
                <div className="data-input">
                  <div className="input-node">Experience</div>
                  <div className="input-node">Capacity</div>
                  <div className="input-node">Regulatory</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="methodology-section">
        <div className="container">
          <h2>Randomization Rate Forecasting in LATAM</h2>
          <div className="methodology-content">
            <div className="method-text">
              <p>
                To improve study design, Lummo forecasts randomization rates based on Latin 
                America-specific historical data. By analyzing past trials with similar eligibility 
                criteria, therapeutic areas, and study sites in the region, Lummo predicts the 
                probability of successful patient randomization considering unique cultural and 
                socioeconomic factors.
              </p>
              <p>
                This is done by training a model on stratified patient cohorts, evaluating 
                LATAM-specific dropout risks, and identifying potential bottlenecks in patient 
                recruitment. The result is an adaptive, data-driven approach to trial planning 
                that ensures adequate enrollment while reducing the probability of underpowered 
                studies or excessive delays.
              </p>
            </div>
            <div className="method-visual">
              <div className="forecasting-chart">
                <div className="data-input">
                  <div className="input-node">Q1</div>
                  <div className="input-node">Q2</div>
                  <div className="input-node">Q3</div>
                  <div className="input-node">Q4</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Agents Section */}
      <section className="ai-agents-section">
        <div className="container">
          <h2>Using AI Agents for LATAM Site Support</h2>
          <p>
            Lummo AI is one of the first multi-agentic platforms in the clinical development space 
            specifically designed for Latin America. Our agents help users save time in the initial 
            stages of site feasibility, considering the regulatory and cultural particularities of 
            each country.
          </p>
          
          <div className="agents-grid">
            <div className="agent-card">
              <h3>LATAM Regulatory Agent</h3>
              <p>
                Specialized in navigating specific regulatory requirements from COFEPRIS, ANVISA, 
                ANMAT, ISP, INVIMA, and other regional agencies.
              </p>
              <div className="agent-capabilities">
                <span className="capability">Local Documentation</span>
                <span className="capability">Approval Timelines</span>
                <span className="capability">Specific Requirements</span>
              </div>
            </div>

            <div className="agent-card">
              <h3>Cultural & Socioeconomic Agent</h3>
              <p>
                Optimizes recruitment strategies considering ethnic diversity, language barriers, 
                and unique socioeconomic factors of each region.
              </p>
              <div className="agent-capabilities">
                <span className="capability">Ethnic Diversity</span>
                <span className="capability">Language Barriers</span>
                <span className="capability">Geographic Access</span>
              </div>
            </div>

            <div className="agent-card">
              <h3>Regional Logistics Agent</h3>
              <p>
                Manages supply chain, drug distribution, and logistics specific to each Latin 
                American country's infrastructure.
              </p>
              <div className="agent-capabilities">
                <span className="capability">Cold Chain</span>
                <span className="capability">Distribution</span>
                <span className="capability">Customs</span>
              </div>
            </div>

            <div className="agent-card">
              <h3>LATAM Financial Agent</h3>
              <p>
                Optimizes budgets considering country-specific costs, currency fluctuations, 
                and local reimbursement structures.
              </p>
              <div className="agent-capabilities">
                <span className="capability">Local Costs</span>
                <span className="capability">Currency Fluctuation</span>
                <span className="capability">Reimbursements</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regional Insights */}
      <section className="regional-insights">
        <div className="container">
          <h2>Regional-Specific Insights</h2>
          <div className="insights-grid">
            <div className="insight-card">
              <div className="insight-header">
                <h3>Mexico</h3>
                <span className="market-size">Market: $2.1B</span>
              </div>
              <div className="insight-stats">
                <div className="stat">
                  <span className="stat-value">180</span>
                  <span className="stat-label">Active Sites</span>
                </div>
                <div className="stat">
                  <span className="stat-value">45 days</span>
                  <span className="stat-label">COFEPRIS Time</span>
                </div>
                <div className="stat">
                  <span className="stat-value">85%</span>
                  <span className="stat-label">Success Rate</span>
                </div>
              </div>
              <p>
                COFEPRIS has implemented digital processes that reduce approval times. High 
                concentration of sites in Mexico City, Guadalajara, and Monterrey.
              </p>
            </div>

            <div className="insight-card">
              <div className="insight-header">
                <h3>Brazil</h3>
                <span className="market-size">Market: $3.8B</span>
              </div>
              <div className="insight-stats">
                <div className="stat">
                  <span className="stat-value">320</span>
                  <span className="stat-label">Active Sites</span>
                </div>
                <div className="stat">
                  <span className="stat-value">60 days</span>
                  <span className="stat-label">ANVISA Time</span>
                </div>
                <div className="stat">
                  <span className="stat-value">78%</span>
                  <span className="stat-label">Success Rate</span>
                </div>
              </div>
              <p>
                ANVISA requires extensive documentation but offers the largest market in LATAM. 
                Unique ethnic diversity requires specialized recruitment strategies.
              </p>
            </div>

            <div className="insight-card">
              <div className="insight-header">
                <h3>Argentina</h3>
                <span className="market-size">Market: $1.4B</span>
              </div>
              <div className="insight-stats">
                <div className="stat">
                  <span className="stat-value">95</span>
                  <span className="stat-label">Active Sites</span>
                </div>
                <div className="stat">
                  <span className="stat-value">90 days</span>
                  <span className="stat-label">ANMAT Time</span>
                </div>
                <div className="stat">
                  <span className="stat-value">72%</span>
                  <span className="stat-label">Success Rate</span>
                </div>
              </div>
              <p>
                ANMAT has slower processes but high-quality sites. Economic volatility requires 
                flexible budget strategies.
              </p>
            </div>

            <div className="insight-card">
              <div className="insight-header">
                <h3>Other LATAM</h3>
                <span className="market-size">Market: $2.7B</span>
              </div>
              <div className="insight-stats">
                <div className="stat">
                  <span className="stat-value">240</span>
                  <span className="stat-label">Active Sites</span>
                </div>
                <div className="stat">
                  <span className="stat-value">75 days</span>
                  <span className="stat-label">Average Time</span>
                </div>
                <div className="stat">
                  <span className="stat-value">68%</span>
                  <span className="stat-label">Success Rate</span>
                </div>
              </div>
              <p>
                Colombia, Chile, Peru, and other countries offer emerging opportunities with 
                evolving regulations and competitive costs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Discussion */}
      <section className="discussion-section">
        <div className="container">
          <h2>Discussion</h2>
          <p>
            By structuring trial data into LATAM-specific modular components, Lummo effectively 
            captures regional design patterns and failure modes, leading to more efficient and 
            informed protocol generation. Randomization rate forecasting and site selection 
            optimization improve feasibility assessments, reducing delays in patient recruitment 
            and region-specific site availability.
          </p>
          <p>
            Additionally, the AI-driven risk mitigation network identifies and mitigates common 
            failure points specific to the Latin American regulatory context, offering a proactive 
            framework for improving trial success rates in the region.
          </p>
          <p>
            The integration of multiple advanced clustering techniques, including frameworks like 
            TCBPMA (Text Clustering Based on Pre-trained Models and Autoencoders) adapted for 
            Spanish and Portuguese data, in clinical trial planning bridges the gap between 
            academic research and real-world applications in Latin America.
          </p>
          <p>
            Future research will focus on forecasting LATAM-specific opportunity areas and 
            incorporating even more patient-generated data into patient population modeling, 
            considering the cultural and socioeconomic particularities of each country in the region.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />
    </div>
    
  );
};

export default TechnicalReport; 